import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { environment } from '@env/environment';
import { NomenclatureService } from '@services/nomenclature.service';
import { defaultIfEmpty } from 'rxjs/operators';
import { Voucher } from '../_models/voucher.model';
import { VoucherServices } from '../_models/voucher-services.model';
import { VoucherTechnicalPoint } from '../_models/voucher-technical-point.model';
import { TechPointService } from '@app/tech-points/_models/tech-point-service.model';
import { VoucherRvsCategory } from '../_models/voucher-rvs-category.model';
import { Page } from '@interfaces/page';
import { ListVoucher } from '../_models/list-vouchers.model';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { VoucherResponse } from '../_models/service-rvs.model';

const apiServerUrl                       = environment.apiBaseURL;
const promotionsUrl                      = apiServerUrl + '/voucher';

const findVoucherByIdURL                         = promotionsUrl + '/{id}';
const findVoucherPageableURL                     = promotionsUrl + '/pageable';
const countVoucherPageableURL                     = promotionsUrl + '/pageable-count';
const findAllValidVouchersURL                    = promotionsUrl + '/promocodes';
const findVoucherServicesByVoucherIdURL           = promotionsUrl + '/{id}/services';
const findVoucherServicesAndRvsByVoucherIdURL    = promotionsUrl + '/services-rvs/{id}';
const findVoucherRvsCategoriesByVoucherIdURL     = promotionsUrl + '/{id}/categories';
const findTechnicalPointsByVoucherIdURL           = promotionsUrl + '/{id}/technical-points';
const findTimeConstraintByTechnicalPointIdURL    = promotionsUrl + '/technical-points-time-constraints';
const findTechnicalPointsByServiceAndCategoryIdURL = promotionsUrl + '/by-service-category-id';
const countStaleVoucherCodesURL                  = promotionsUrl + '/{id}/stale-codes-count';
const refreshVoucherCodesURL                     = promotionsUrl + '/{id}/refresh-codes';

@Injectable({
  providedIn: 'root'
})
export class VoucherService {

  constructor(private http: HttpClient, private nomenclatureService: NomenclatureService) { }

  public findVoucherById(id: number): Observable<Voucher> {
    if (id == null) return this.returnEmptyObservable();
    return this.http.get<Voucher>(findVoucherByIdURL.replace('{id}', `${id}`));
  }

  public getVoucherCodeById(voucherCodeId: number): Observable<any> {
    if (voucherCodeId == null) return this.returnEmptyObservable();
    const url = promotionsUrl + `/voucher-code/${voucherCodeId}`;
    let params = new HttpParams().set('voucherCodeId', voucherCodeId.toString());
    return this.http.get<any>(url, { params });
  }

  private returnEmptyObservable() {
    return EMPTY.pipe(
      defaultIfEmpty()
    );
  }

  public getPromotionsNomenclature(id: number) {
    return forkJoin([
     this.nomenclatureService.getRvsCategoryServicesByServiceId(id),
     this.nomenclatureService.findAllValidServices()
    ]);
  }

  public findServicesByVoucherId(id: number): Observable<VoucherServices[]> {
    if (id == null) {
      return EMPTY.pipe(defaultIfEmpty());
    }
    
    return this.http.get<VoucherServices[]>(findVoucherServicesByVoucherIdURL.replace('{id}', `${id}`));
  }

  public findServicesAndRvsByVoucherId(id: number): Observable<VoucherResponse> {
    if (id == null) {
      return EMPTY.pipe(defaultIfEmpty());    
    }
    
    const url = findVoucherServicesAndRvsByVoucherIdURL.replace('{id}', `${id}`);
    return this.http.get<VoucherResponse>(url);
  }

  public findServicesByVoucherIdToPromise(id: number) {
    return this.findServicesByVoucherId(id).toPromise()
  }

  public findVoucherRvsCategoriesByVoucherId(id: number | undefined): Observable<VoucherRvsCategory[]> {
    return this.http.get<VoucherRvsCategory[]>(findVoucherRvsCategoriesByVoucherIdURL.replace('{id}', `${id}`));
  }

  public findTechnicalPointByVoucherId(id: number): Observable<VoucherTechnicalPoint[]> {
    if (id == null) {
      return EMPTY.pipe(defaultIfEmpty());
    }

    return this.http.get<VoucherTechnicalPoint[]>(findTechnicalPointsByVoucherIdURL.replace('{id}', `${id}`));
  }

  public findTimeConstraintsByTechnicalPointId(id: number): Observable<any> {
    let requestParams = new HttpParams();
    requestParams = requestParams.set("id", id.toString());
    
    return this.http.get<any>(findTimeConstraintByTechnicalPointIdURL, {params: requestParams});
  }

  public findAllValidVouchers(): Observable<Voucher[]> {
    return this.http.get<Voucher[]>(findAllValidVouchersURL);
  }

  public findTechnicalPointsByServiceAndCategoryId(serviceCategoryIds: Array<number>): Observable<TechPointService[]> {
    let requestParams = new HttpParams();
    requestParams = requestParams.set("serviceCategoryIds", serviceCategoryIds.toString());

    return this.http.get<any>(findTechnicalPointsByServiceAndCategoryIdURL, {params: requestParams});
  }

  public saveVoucher(voucher: Voucher, deactivate: boolean) {
    let requestParams = new HttpParams();
    requestParams = requestParams.set("deactivate", deactivate.toString());

    return this.http.post(promotionsUrl, voucher, {params: requestParams});
  }

  /**
   * Number of already issued voucher codes that still point at an older version of this voucher.
   * Editing an active voucher creates a new version, which leaves existing codes behind.
   */
  public countStaleVoucherCodes(id: number): Observable<number> {
    if (id == null) return this.returnEmptyObservable();
    return this.http.get<number>(countStaleVoucherCodesURL.replace('{id}', `${id}`));
  }

  /** Re-points the still usable voucher codes at the current version. Returns how many were updated. */
  public refreshVoucherCodes(id: number): Observable<number> {
    return this.http.post<number>(refreshVoucherCodesURL.replace('{id}', `${id}`), null);
  }

  public initializeVoucherServices(id: number) {
    return Promise.all([
      this.findServicesByVoucherId(id).toPromise(),
      this.nomenclatureService.getRvsCategoriesForPromoServices().toPromise(),
      this.nomenclatureService.findAllValidServicesForPromo().toPromise()
    ]);
  }

  public initializeVoucherTechPoints(id: number) {
    return Promise.all([
      this.findTechnicalPointByVoucherId(id).toPromise()
    ]);
  }

  public pageable(sortingPaging: SortingPagingData, searchForm: any) {
    return forkJoin([
      this.findAllByFilter(sortingPaging, searchForm),
      this.countAllByFilter(sortingPaging, searchForm)
    ]);
  }

  public findAllByFilter(sortingPaging: SortingPagingData, searchForm: any): Observable<Page<ListVoucher>> {
    return this.http.get<Page<ListVoucher>>(findVoucherPageableURL, { params : this.constructPageableHttpParams(sortingPaging, searchForm) });
  }

  public countAllByFilter(sortingPaging: SortingPagingData, searchForm: any): Observable<number> {
    return this.http.get<number>(countVoucherPageableURL, { params : this.constructPageableHttpParams(sortingPaging, searchForm) });
  }

  private constructPageableHttpParams(sortingPaging: SortingPagingData, searchForm: any) {
    let params = new HttpParams().set('pageSize', sortingPaging.pageSize.toString())
                                 .set('page', sortingPaging.pageNumber.toString());
                                 
     if (sortingPaging.isSortingValid()) {
      params = params
        .set("sortBy", sortingPaging.sortBy)
        .set("sortAsc", sortingPaging.sortAsc === null ? 'null' : (sortingPaging.sortAsc ? 'true' : 'false'));
    }

    if (searchForm.city?.code != null) {
      params = params.set('cityCode', searchForm.city?.code);
    }

    if (searchForm.status?.id != null) {
      params = params.set('statusId', searchForm.status?.id);
    }

    if (searchForm.name != null) {
      params = params.set('name', searchForm.name);
    }

    if (searchForm.technicalPoint?.mobileAppId != null) {
      params = params.set('technicalPointId', searchForm.technicalPoint.mobileAppId!);
    }

    if (searchForm.service != null) {
      params = params.set('serviceId', searchForm.service.id);
    }

    return params;
  }

}
